if (!customElements.get('variant-selects-v2')) {
  const findById = (root, id) => {
    if (!root || !id) return null;

    const escapedId = String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return root.querySelector(`[id="${escapedId}"]`);
  };

  /**
   *  @class
   *  @function VariantSelectsV2
   */
  class VariantSelectsV2 extends HTMLElement {
    constructor() {
      super();
      this.isDisabledFeature = this.dataset.isDisabled;
      this.addEventListener('change', this.onVariantChange.bind(this));
    }

    connectedCallback() {
      this.setScope();
      this.updateOptions();
      this.updateMasterId();
      this.setDisabled();
      this.setImageSet();
    }

    setScope() {
      this.productUpsell = this.closest('.product-upsell');
      this.scope = this.productUpsell || this;
      this.productWrapper = this.scope.querySelector('.thb-product-detail');
      this.productSlider = findById(this.scope, `MediaGallery-${this.dataset.section}`);
      this.hideVariants = this.productSlider ? this.productSlider.dataset.hideVariants === 'true' : false;
    }

    onVariantChange() {
      this.setScope();
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.removeErrorMessage();
      this.updateVariantText();
      this.setDisabled();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateVariantInput();
        this.renderProductInfo();
      }

      this.dispatchEvent(new CustomEvent('product-upsell:variant-change', {
        detail: {
          variant: this.currentVariant,
          sectionId: this.dataset.section,
          productHandle: this.productUpsell ? this.productUpsell.dataset.handle : undefined
        },
        bubbles: true
      }));
    }

    updateOptions() {
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = [];
      this.option_keys = [];
      this.fieldsets.forEach((fieldset) => {
        if (fieldset.querySelector('select')) {
          this.options.push(fieldset.querySelector('select').value);
          this.option_keys.push(fieldset.querySelector('select').name);
        } else if (fieldset.querySelectorAll('input').length) {
          const checkedInput = fieldset.querySelector('input:checked');
          if (checkedInput) {
            this.options.push(checkedInput.value);
            this.option_keys.push(checkedInput.name);
          }
        }
      });
    }

    updateVariantText() {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      fieldsets.forEach((item, i) => {
        let label = item.querySelector('.form__label__value');
        if (label) {
          label.innerHTML = this.options[i];
        }
      });
    }

    updateMasterId() {
      this.currentVariant = this.getVariantData().find((variant) => {
        return !variant.options
          .map((option, index) => {
            return this.options[index] === option;
          })
          .includes(false);
      });
    }

    updateMedia() {
      if (!this.currentVariant) return;
      if (!this.currentVariant.featured_media) return;
      if (!this.productSlider) return;

      let mediaId = `${this.dataset.section}-${this.currentVariant.featured_media.id}`;
      let activeMedia = this.productSlider.querySelector(
        `[data-media-id="${mediaId}"]`,
      );

      if (!activeMedia) return;

      this.productSlider
        .querySelectorAll("[data-media-id]")
        .forEach((element) => {
          element.classList.remove("is-active");
        });

      this.setImageSetMedia();
      activeMedia.classList.add("is-active");
      activeMedia.parentElement.prepend(activeMedia);

      window.setTimeout(() => {
        activeMedia.parentElement.scrollTo({
          left: 0,
          top: 0,
          behavior: "smooth",
        });
      });
    }

    updateVariantInput() {
      const productForms = [
        findById(this.scope, `product-form-${this.dataset.section}`),
        findById(this.scope, 'product-form-installment')
      ].filter(Boolean);
      const upsellCheckbox = this.scope.querySelector('.product-upsell-checkbox');

      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (input) {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      });

      if (upsellCheckbox) {
        upsellCheckbox.value = this.currentVariant.id;
        upsellCheckbox.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }

    removeErrorMessage() {
      const productForm = this.scope.querySelector("product-form");
      if (productForm && typeof productForm.handleErrorMessage === "function") {
        productForm.handleErrorMessage();
      }
    }

    getSectionsToRender() {
      return [
        `price-${this.dataset.section}`,
        `inventory-${this.dataset.section}`,
        `sku-${this.dataset.section}`,
        `quantity-${this.dataset.section}`,
      ];
    }

    renderProductInfo() {
      let sections = this.getSectionsToRender();

      fetch(
        `${this.dataset.url}?variant=${this.currentVariant.id}&section_id=${this.dataset.section}`,
      )
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(
            responseText,
            "text/html",
          );
          sections.forEach((id) => {
            const destination = findById(this.scope, id);
            const source = findById(html, id);

            if (source && destination) destination.innerHTML = source.innerHTML;

            if (destination) destination.classList.remove("visibility-hidden");
          });
          this.toggleAddButton(
            !this.currentVariant.available,
            window.theme.variantStrings.soldOut,
          );
        });
    }

    toggleAddButton(disable = true, text = false, modifyClass = true) {
      const submitButtons = this.scope.querySelectorAll(
        ".single-add-to-cart-button",
      );
      if (!submitButtons.length) return;

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector(
          ".single-add-to-cart-button--text",
        );
        if (!submitButtonText) return;

        if (disable) {
          submitButton.setAttribute("disabled", "disabled");
          if (text) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute("disabled");
          submitButton.classList.remove("loading");
          if (modifyClass) submitButton.classList.remove("sold-out");
          submitButtonText.textContent = window.theme.variantStrings.addToCart;
        }
      });
    }

    setUnavailable() {
      const submitButtons = this.scope.querySelectorAll(
        ".single-add-to-cart-button",
      );
      const price = findById(this.scope, `price-${this.dataset.section}`);

      submitButtons.forEach((submitButton) => {
        const submitButtonText = submitButton.querySelector(
          ".single-add-to-cart-button--text",
        );
        if (!submitButtonText) return;
        submitButtonText.textContent = window.theme.variantStrings.select_an_option;
        submitButton.setAttribute("disabled", "disabled");
        submitButton.classList.add("sold-out");
      });
      if (price) price.classList.add("visibility-hidden");
    }

    setDisabled() {
      if (this.isDisabledFeature != "true") return;
      const variant_data = this.getVariantData();
      if (!variant_data) return;

      this.fieldsets.forEach((fieldset, i) => {
        const elements = fieldset.querySelectorAll(
          'input[type="radio"], option',
        );

        elements.forEach((element) => {
          const elementValue = element.value;

          // 1. Check if the variant combination exists in your Shopify data
          const exists = variant_data.some((variant) => {
            if (i === 0) return variant.option1 === elementValue;
            if (i === 1) return variant.option1 === this.options[0] && variant.option2 === elementValue;
            if (i === 2) return variant.option1 === this.options[0] && variant.option2 === this.options[1] && variant.option3 === elementValue;
            return false;
          });

          // 2. Check if the variant combination is currently available (in stock)
          const available = variant_data.some((variant) => {
            if (!variant.available) return false;
            if (i === 0) return variant.option1 === elementValue;
            if (i === 1) return variant.option1 === this.options[0] && variant.option2 === elementValue;
            if (i === 2) return variant.option1 === this.options[0] && variant.option2 === this.options[1] && variant.option3 === elementValue;
            return false;
          });

          const doesNotExist = !exists;
          const isOutOfStock = exists && !available;

          if (element.tagName === "OPTION") {
            let baseText = elementValue;

            if (doesNotExist) {
              element.disabled = true;
              element.hidden = true;
              element.innerHTML = baseText;
            } else if (isOutOfStock) {
              element.disabled = false;
              element.hidden = false;
              element.innerHTML = `${baseText} - Sold Out`;
            } else {
              element.disabled = false;
              element.hidden = false;
              element.innerHTML = baseText;
            }
          } else {
            // Apply the 'is-disabled' class to the INPUT for out-of-stock
            // (This matches your original theme logic to cross it out)
            element.classList.toggle("is-disabled", isOutOfStock);

            // For non-existent variants, target the LABEL to completely hide the UI element
            const label = fieldset.querySelector(`label[for="${element.id}"]`) || element.closest('label');
            if (label) {
              label.style.display = doesNotExist ? "none" : "";
            }
          }
        });
      });
      return true;
    }

    getImageSetName(variant_name) {
      if (!variant_name) return "";
      return variant_name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/-$/, "")
        .replace(/^-/, "");
    }

    setImageSet() {
      if (!this.productSlider) return;

      let dataSetEl = this.productSlider.querySelector("[data-set-name]");
      if (dataSetEl) {
        this.imageSetName = dataSetEl.dataset.setName;
        let inputHandle = this.querySelector(
          '.product-form__input[data-handle="' + this.imageSetName + '"]',
        );
        if (inputHandle) {
          this.imageSetIndex = inputHandle.dataset.index;
          this.setImageSetMedia();
        }
      }
    }

    setImageSetMedia() {
      if (!this.imageSetIndex || !this.currentVariant) return;

      let variantProperty = this.currentVariant[this.imageSetIndex];
      if (!variantProperty) return;

      let setValue = this.getImageSetName(variantProperty);
      let group = this.imageSetName + "_" + setValue;
      let selected_set_images = this.productSlider.querySelectorAll(
        `[data-set-name="${this.imageSetName}"]`,
      );

      if (this.hideVariants) {
        selected_set_images.forEach((thumb) => {
          thumb.classList.toggle("is-active", thumb.dataset.group === group);
        });
      } else {
        let set_images = Array.from(selected_set_images).filter(
          function (element) {
            return element.dataset.group === group;
          },
        );
        set_images.forEach((thumb) => {
          thumb.parentElement.prepend(thumb);
        });
      }
    }

    getVariantData() {
      this.variantData =
        this.variantData ||
        JSON.parse(this.querySelector('[type="application/json"]').textContent);
      return this.variantData;
    }
  }

  customElements.define('variant-selects-v2', VariantSelectsV2);
}

(function () {
  if (window.ProductUpsellLoader) {
    window.ProductUpsellLoader.load(document);
    return;
  }

  const selector = '.product-upsell[data-handle]';
  const siblingSelector = '.product-upsell .variations--siblings .product-form__input--siblings a[href]';

  function getProductUrl(handle) {
    const rootUrl = window.theme && window.theme.routes ? window.theme.routes.root_url : '/';
    const root = rootUrl.replace(/\/$/, '');

    return `${root}/products/${encodeURIComponent(handle)}?view=v2-quick-view`;
  }

  function getHandleFromUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.origin);
      const parts = parsedUrl.pathname.split('/').filter(Boolean);
      const productIndex = parts.indexOf('products');

      if (productIndex === -1 || !parts[productIndex + 1]) {
        return '';
      }

      return decodeURIComponent(parts[productIndex + 1]);
    } catch (error) {
      return '';
    }
  }

  function getLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.className = 'loading-overlay product-upsell__loading-overlay';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = `
      <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="spinner-path" fill="none" stroke-width="6" cx="33" cy="33" r="30" stroke="var(--color-accent)"></circle>
      </svg>
    `;

    return overlay;
  }

  function setLoading(container, isLoading) {
    container.classList.toggle('loading', isLoading);
    container.toggleAttribute('aria-busy', isLoading);

    if (!isLoading) {
      container.querySelector(':scope > .product-upsell__loading-overlay')?.remove();
      return;
    }

    if (!container.querySelector(':scope > .product-upsell__loading-overlay')) {
      container.appendChild(getLoadingOverlay());
    }
  }

  function runExternalScripts(container) {
    const scripts = container.querySelectorAll('script[src]');

    scripts.forEach((sourceScript) => {
      const script = document.createElement('script');
      script.src = sourceScript.src;
      script.defer = sourceScript.defer;
      document.head.appendChild(script);
    });
  }

  function renderUpsell(container, html, productUrl) {
    const content = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#Product-Upsell-Content');

    if (!content) {
      container.dataset.productUpsellState = 'error';
      setLoading(container, false);
      return;
    }

    container.innerHTML = content.innerHTML;
    container.dataset.productUpsellState = 'loaded';
    setLoading(container, false);
    runExternalScripts(container);

    setTimeout(() => {
      if (window.Shopify && Shopify.PaymentButton) {
        Shopify.PaymentButton.init();
      }
      if (window.ProductModel) {
        window.ProductModel.loadShopifyXR();
      }
    }, 300);

    if (typeof dispatchCustomEvent === 'function') {
      dispatchCustomEvent('product-upsell:load', {
        productUrl,
        productHandle: container.dataset.handle
      });
    }
  }

  function loadUpsell(container, force = false) {
    const handle = container.dataset.handle;

    if (!handle || (!force && container.dataset.productUpsellState)) {
      return;
    }

    const productUrl = getProductUrl(handle);
    container.dataset.productUpsellState = 'loading';
    setLoading(container, true);

    fetch(productUrl, {
      method: 'GET',
      credentials: 'same-origin'
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Product upsell request failed: ${response.status}`);
        }

        return response.text();
      })
      .then((html) => {
        renderUpsell(container, html, productUrl);
      })
      .catch(() => {
        container.dataset.productUpsellState = 'error';
        setLoading(container, false);
      });
  }

  function onSiblingClick(event) {
    const link = event.target.closest(siblingSelector);
    if (!link) return;

    const container = link.closest(selector);
    if (!container) return;

    const handle = getHandleFromUrl(link.href);
    if (!handle) return;

    event.preventDefault();

    if (handle === container.dataset.handle) {
      return;
    }

    container.dataset.handle = handle;
    loadUpsell(container, true);
  }

  function loadProductUpsells(root) {
    if (root.matches && root.matches(selector)) {
      loadUpsell(root);
    }

    root.querySelectorAll(selector).forEach(loadUpsell);
  }

  window.ProductUpsellLoader = {
    load: loadProductUpsells,
    reload: (container) => loadUpsell(container, true)
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      loadProductUpsells(document);
    });
  } else {
    loadProductUpsells(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    loadProductUpsells(event.target);
  });

  document.addEventListener('click', onSiblingClick);
})();
